import React, { useEffect, useState, useCallback } from "react";
import { onBackdropMouseDown, onModalPanelMouseDown } from "../lib/modal";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";
import { fmt } from "@/lib/format";
import { toast } from "sonner";
import { Plus, Trash2, CheckCircle, RotateCcw, ChevronDown, ChevronUp, Users as UsersIcon, Wallet, TrendingUp, TrendingDown, Download, Printer } from "lucide-react";
import { printReport } from "@/lib/printReport";
import { usePrintTemplate } from "@/hooks/usePrintTemplate";
import { localizeError } from "@/lib/errors";
import { useConfirmDialog } from "@/hooks/useConfirmDialog";

const monthOptions = () => {
  const out = [];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    out.push(d.toISOString().slice(0, 7));
  }
  return out;
};

const downloadCsv = (filename, rows) => {
  const csv = rows.map(r => r.map(c => {
    const s = String(c ?? "");
    return s.includes(",") || s.includes("\"") || s.includes("\n") ? `"${s.replace(/"/g, '""')}"` : s;
  }).join(",")).join("\n");
  const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
};

export default function Payroll() {
  const { t, lang } = useLanguage();
  const { confirm, ConfirmDialogSlot } = useConfirmDialog();
  const template = usePrintTemplate();
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [data, setData] = useState({ rows: [], totals: {} });
  const [expanded, setExpanded] = useState({});
  const [adjModalOpen, setAdjModalOpen] = useState(null); // employee_id
  const [adjForm, setAdjForm] = useState({ type: "deduction", amount: 0, reason: "" });

  const load = useCallback(async () => {
    const r = await api.get(`/payroll?month=${month}`);
    setData(r.data);
  }, [month]);
  useEffect(() => { load(); }, [load]);

  const addAdjustment = async () => {
    if (!parseFloat(adjForm.amount)) { toast.error(lang === "ar" ? "أدخل قيمة" : "Enter amount"); return; }
    try {
      await api.post(`/payroll/${adjModalOpen}/adjustments`, { ...adjForm, amount: parseFloat(adjForm.amount), month });
      toast.success(lang === "ar" ? "تم الحفظ" : "Saved");
      setAdjModalOpen(null);
      setAdjForm({ type: "deduction", amount: 0, reason: "" });
      load();
    } catch (e) { toast.error(localizeError(e, t, lang)); }
  };

  const delAdjustment = async (emp_id, adj_id) => {
    const ok = await confirm({
      title: lang === "ar" ? "حذف القيد؟" : "Delete?",
      confirmLabel: lang === "ar" ? "حذف" : "Delete",
      cancelLabel: lang === "ar" ? "إلغاء" : "Cancel",
      variant: "danger",
    });
    if (!ok) return;
    try { await api.delete(`/payroll/${emp_id}/adjustments/${adj_id}`); load(); }
    catch (e) { toast.error(localizeError(e, t, lang)); }
  };

  const confirmPayroll = async (emp_id) => {
    const ok = await confirm({
      title: lang === "ar"
        ? "تأكيد الراتب لهذا الشهر؟ سيتم إنشاء قيد مصروف تلقائياً تحت بند 'رواتب' ولا يمكن التعديل بعدها إلا بإلغاء التأكيد."
        : "Confirm payroll? This creates an auto-expense under 'Salaries' and locks the month.",
      confirmLabel: lang === "ar" ? "تأكيد" : "Confirm",
      cancelLabel: lang === "ar" ? "إلغاء" : "Cancel",
      variant: "primary",
    });
    if (!ok) return;
    try {
      const r = await api.post(`/payroll/${emp_id}/confirm`, { month });
      toast.success(lang === "ar" ? `تم التأكيد. رقم القيد: ${r.data.expense.expense_number}` : `Confirmed: ${r.data.expense.expense_number}`);
      load();
    } catch (e) { toast.error(localizeError(e, t, lang)); }
  };

  const unconfirmPayroll = async (emp_id) => {
    const ok = await confirm({
      title: lang === "ar" ? "إلغاء التأكيد؟ سيتم حذف المصروف التلقائي." : "Unconfirm? Auto-expense will be deleted.",
      confirmLabel: lang === "ar" ? "تأكيد" : "Confirm",
      cancelLabel: lang === "ar" ? "إلغاء" : "Cancel",
      variant: "danger",
    });
    if (!ok) return;
    try { await api.post(`/payroll/${emp_id}/unconfirm`, { month }); load(); }
    catch (e) { toast.error(localizeError(e, t, lang)); }
  };

  // Completion pass — Export CSV + Print
  const exportCsv = () => {
    const rows = [["Month", "Employee", "Base", "Allowances", "Deductions", "Bonuses", "Net", "Status"]];
    for (const r of data.rows) {
      rows.push([r.month, r.employee_name, r.base, r.allowances, r.total_deductions, r.total_bonuses, r.net, r.status]);
    }
    rows.push([]);
    rows.push(["TOTALS", "", data.totals.base || 0, data.totals.allowances || 0, data.totals.deductions || 0, data.totals.bonuses || 0, data.totals.net || 0, ""]);
    downloadCsv(`payroll_${month}.csv`, rows);
    toast.success(lang === "ar" ? "تم التصدير" : "Exported");
  };
  const printReportPro = () => {
    printReport({
      title: lang === "ar" ? "كشف الرواتب الشهري" : "Monthly Payroll Sheet",
      subtitle: month,
      lang,
      template,
      meta: [
        { label: lang === "ar" ? "الشهر" : "Month", value: month },
        { label: lang === "ar" ? "عدد الموظفين" : "Employees", value: String(data.rows.length) },
        { label: lang === "ar" ? "مؤكد" : "Confirmed", value: String(data.totals.confirmed_count || 0) },
        { label: lang === "ar" ? "مسودات" : "Drafts", value: String(data.totals.draft_count || 0) },
      ],
      columns: [
        lang === "ar" ? "الموظف" : "Employee",
        lang === "ar" ? "الأساسي" : "Base",
        lang === "ar" ? "البدلات" : "Allowances",
        lang === "ar" ? "الخصومات" : "Deductions",
        lang === "ar" ? "المكافآت" : "Bonuses",
        lang === "ar" ? "الصافي" : "Net",
        lang === "ar" ? "الحالة" : "Status",
      ],
      rows: data.rows.map(r => [
        r.employee_name,
        { value: r.base.toFixed(2), className: "num" },
        { value: r.allowances.toFixed(2), className: "num muted" },
        { value: r.total_deductions.toFixed(2), className: "num neg" },
        { value: r.total_bonuses.toFixed(2), className: "num pos" },
        { value: r.net.toFixed(2), className: "num pos" },
        {
          value: r.status === "confirmed"
            ? `<span class="badge badge-ok">${lang === "ar" ? "مؤكد" : "Confirmed"}</span>`
            : `<span class="badge badge-warn">${lang === "ar" ? "مسودة" : "Draft"}</span>`,
          html: true,
        },
      ]),
      totals: [
        { label: lang === "ar" ? "إجمالي الأساسي" : "Total base", value: (data.totals.base || 0).toFixed(2) },
        { label: lang === "ar" ? "إجمالي الخصومات" : "Total deductions", value: (data.totals.deductions || 0).toFixed(2), className: "rose" },
        { label: lang === "ar" ? "إجمالي المكافآت" : "Total bonuses", value: (data.totals.bonuses || 0).toFixed(2), className: "gold" },
        { label: lang === "ar" ? "صافي الإجمالي" : "Total net", value: (data.totals.net || 0).toFixed(2), className: "gold" },
      ],
      footerNote: lang === "ar" ? "كشف الرواتب — للاستخدام الداخلي" : "Payroll sheet — internal use",
    });
  };

  return (
    <div className="space-y-6" data-testid="payroll-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-heading text-4xl gold-text">{lang === "ar" ? "الرواتب (HR Lite)" : "Payroll (HR Lite)"}</h1>
        <div className="flex items-center gap-2 flex-wrap no-print">
          <label className="text-sm text-[var(--text-muted)]">{lang === "ar" ? "الشهر" : "Month"}</label>
          <select value={month} onChange={e => setMonth(e.target.value)} data-testid="payroll-month"
            className="input-dark num-display">
            {monthOptions().map(m => <option key={m} value={m}>{m}</option>)}
          </select>
          <button onClick={exportCsv} data-testid="payroll-export-csv"
            className="px-3 py-1.5 rounded-md text-sm border border-[var(--border)] hover:border-[var(--gold)] bg-[var(--surface)] text-[var(--text)] flex items-center gap-1">
            <Download size={13}/>CSV
          </button>
          <button onClick={printReportPro} data-testid="payroll-print"
            className="px-3 py-1.5 rounded-md text-sm border border-[var(--border)] hover:border-[var(--gold)] bg-[var(--surface)] text-[var(--text)] flex items-center gap-1">
            <Printer size={13}/>{lang === "ar" ? "طباعة" : "Print"}
          </button>
        </div>
      </div>

      <div className="card-surface p-4 text-sm text-[var(--text-muted)]">
        {lang === "ar" ? "الرواتب الأساسية فقط. " : "Base salary only. "}
        <Link to="/staff-payouts" className="text-[var(--gold)] hover:underline">{lang === "ar" ? "صرف العمولات والإكراميات ←" : "Staff payouts (commission & tips) →"}</Link>
      </div>

      {/* Totals strip */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <div className="card-surface p-4">
          <div className="text-[10px] uppercase tracking-wide text-[var(--text-muted)] flex items-center gap-1"><UsersIcon size={11}/>{lang === "ar" ? "الموظفون" : "Employees"}</div>
          <div className="text-2xl font-bold mt-1">{data.rows.length}</div>
        </div>
        <div className="card-surface p-4">
          <div className="text-[10px] uppercase tracking-wide text-[var(--text-muted)]">{lang === "ar" ? "الأساسي" : "Base"}</div>
          <div className="text-2xl font-bold mt-1 num-display">{fmt(data.totals.base)}</div>
        </div>
        <div className="card-surface p-4">
          <div className="text-[10px] uppercase tracking-wide text-[var(--text-muted)] flex items-center gap-1"><TrendingDown size={11}/>{lang === "ar" ? "الخصومات" : "Deductions"}</div>
          <div className="text-2xl font-bold mt-1 rose-text num-display">{fmt(data.totals.deductions)}</div>
        </div>
        <div className="card-surface p-4">
          <div className="text-[10px] uppercase tracking-wide text-[var(--text-muted)] flex items-center gap-1"><TrendingUp size={11}/>{lang === "ar" ? "المكافآت" : "Bonuses"}</div>
          <div className="text-2xl font-bold mt-1 gold-text num-display">{fmt(data.totals.bonuses)}</div>
        </div>
        <div className="card-surface p-4">
          <div className="text-[10px] uppercase tracking-wide text-[var(--text-muted)] flex items-center gap-1"><Wallet size={11}/>{lang === "ar" ? "صافي الإجمالي" : "Net Total"}</div>
          <div className="text-2xl font-bold mt-1 gold-text num-display" data-testid="payroll-net-total">{fmt(data.totals.net)}</div>
        </div>
      </div>

      {/* Rows */}
      <div className="card-surface p-4">
        <table className="table-dark">
          <thead>
            <tr>
              <th className="w-8"></th>
              <th>{lang === "ar" ? "الموظف" : "Employee"}</th>
              <th>{lang === "ar" ? "الأساسي" : "Base"}</th>
              <th>{lang === "ar" ? "البدلات" : "Allowances"}</th>
              <th>{lang === "ar" ? "الخصومات" : "Deductions"}</th>
              <th>{lang === "ar" ? "المكافآت" : "Bonuses"}</th>
              <th>{lang === "ar" ? "الصافي" : "Net"}</th>
              <th>{lang === "ar" ? "الحالة" : "Status"}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {data.rows.map(r => {
              const ex = expanded[r.employee_id];
              const confirmed = r.status === "confirmed";
              return (
                <React.Fragment key={r.employee_id}>
                  <tr data-testid={`payroll-row-${r.employee_id}`}>
                    <td>
                      <button onClick={() => setExpanded(p => ({ ...p, [r.employee_id]: !p[r.employee_id] }))}
                        className="p-1 rounded hover:bg-[var(--bg)]" data-testid={`expand-${r.employee_id}`}>
                        {ex ? <ChevronUp size={14}/> : <ChevronDown size={14}/>}
                      </button>
                    </td>
                    <td className="font-medium text-[var(--text)]">{r.employee_name}</td>
                    <td className="num-display">{fmt(r.base)}</td>
                    <td className="num-display text-[var(--text-muted)]">{fmt(r.allowances)}</td>
                    <td className="num-display rose-text">{fmt(r.total_deductions)}</td>
                    <td className="num-display gold-text">{fmt(r.total_bonuses)}</td>
                    <td className="num-display font-bold gold-text">{fmt(r.net)}</td>
                    <td>{confirmed
                      ? <span className="badge badge-green">{lang === "ar" ? "مؤكد" : "Confirmed"}</span>
                      : <span className="badge">{lang === "ar" ? "مسودة" : "Draft"}</span>}</td>
                    <td className="text-end whitespace-nowrap">
                      {!confirmed && (
                        <>
                          <button onClick={() => { setAdjModalOpen(r.employee_id); setAdjForm({ type: "deduction", amount: 0, reason: "" }); }}
                            data-testid={`add-adj-${r.employee_id}`}
                            className="btn-soft px-2 py-1 rounded text-xs me-1 inline-flex items-center gap-1">
                            <Plus size={12}/>{lang === "ar" ? "قيد" : "Adjust"}
                          </button>
                          <button onClick={() => confirmPayroll(r.employee_id)}
                            data-testid={`confirm-${r.employee_id}`}
                            className="btn-gold px-2 py-1 rounded text-xs inline-flex items-center gap-1">
                            <CheckCircle size={12}/>{lang === "ar" ? "تأكيد" : "Confirm"}
                          </button>
                        </>
                      )}
                      {confirmed && (
                        <button onClick={() => unconfirmPayroll(r.employee_id)}
                          data-testid={`unconfirm-${r.employee_id}`}
                          className="px-2 py-1 rounded text-xs border border-[var(--border)] hover:border-rose-500 inline-flex items-center gap-1">
                          <RotateCcw size={12}/>{lang === "ar" ? "إلغاء التأكيد" : "Unconfirm"}
                        </button>
                      )}
                    </td>
                  </tr>
                  {ex && (
                    <tr className="bg-[var(--bg)]/50">
                      <td colSpan={9} className="p-3">
                        <div className="text-[11px] text-[var(--text-muted)] mb-2 ps-2">
                          {lang === "ar" ? "تفصيل الخصومات والمكافآت" : "Deductions & bonuses breakdown"}
                        </div>
                        {(r.deductions.length + r.bonuses.length) === 0 ? (
                          <div className="text-sm text-[var(--text-muted)] py-2 ps-2">{lang === "ar" ? "لا توجد قيود" : "No adjustments"}</div>
                        ) : (
                          <div className="grid md:grid-cols-2 gap-3">
                            <div>
                              <div className="text-xs rose-text font-medium mb-1">{lang === "ar" ? "خصومات" : "Deductions"}</div>
                              {r.deductions.map(a => (
                                <div key={a.adjustment_id} className="flex justify-between items-center text-sm py-1 border-b border-[var(--border-subtle)]">
                                  <span className="text-[var(--text)]">{a.reason || "-"}</span>
                                  <div className="flex items-center gap-2">
                                    <span className="num-display rose-text">−{fmt(a.amount)}</span>
                                    {!confirmed && <button onClick={() => delAdjustment(r.employee_id, a.adjustment_id)} className="text-rose-500"><Trash2 size={12}/></button>}
                                  </div>
                                </div>
                              ))}
                            </div>
                            <div>
                              <div className="text-xs gold-text font-medium mb-1">{lang === "ar" ? "مكافآت" : "Bonuses"}</div>
                              {r.bonuses.map(a => (
                                <div key={a.adjustment_id} className="flex justify-between items-center text-sm py-1 border-b border-[var(--border-subtle)]">
                                  <span className="text-[var(--text)]">{a.reason || "-"}</span>
                                  <div className="flex items-center gap-2">
                                    <span className="num-display gold-text">+{fmt(a.amount)}</span>
                                    {!confirmed && <button onClick={() => delAdjustment(r.employee_id, a.adjustment_id)} className="text-rose-500"><Trash2 size={12}/></button>}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
            {data.rows.length === 0 && (
              <tr><td colSpan={9} className="text-center text-[var(--text-muted)] py-8">{lang === "ar" ? "لا يوجد موظفون نشطون" : "No active employees"}</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Adjustment modal */}
      {adjModalOpen && (
        <div className="fixed inset-0 z-50 bg-[#2B1C1C]/30 backdrop-blur-sm flex items-center justify-center p-4" onMouseDown={(e) => onBackdropMouseDown(e, () => setAdjModalOpen(null))}>
          <div className="card-surface p-6 w-full max-w-md space-y-4" onMouseDown={onModalPanelMouseDown} data-testid="adj-modal">
            <h2 className="font-heading text-2xl gold-text">{lang === "ar" ? "قيد مالي" : "Adjustment"}</h2>
            <div className="space-y-3">
              <div>
                <label className="text-xs text-[var(--text-muted)]">{lang === "ar" ? "النوع" : "Type"}</label>
                <select className="input-dark" value={adjForm.type}
                  onChange={e => setAdjForm({ ...adjForm, type: e.target.value })} data-testid="adj-type">
                  <option value="deduction">{lang === "ar" ? "خصم" : "Deduction"}</option>
                  <option value="bonus">{lang === "ar" ? "مكافأة" : "Bonus"}</option>
                </select>
              </div>
              <div>
                <label className="text-xs text-[var(--text-muted)]">{lang === "ar" ? "القيمة" : "Amount"}</label>
                <input type="number" step="0.01" className="input-dark num-display" value={adjForm.amount}
                  onChange={e => setAdjForm({ ...adjForm, amount: e.target.value })} data-testid="adj-amount"/>
              </div>
              <div>
                <label className="text-xs text-[var(--text-muted)]">{lang === "ar" ? "السبب" : "Reason"}</label>
                <input className="input-dark" value={adjForm.reason}
                  onChange={e => setAdjForm({ ...adjForm, reason: e.target.value })} data-testid="adj-reason"/>
              </div>
            </div>
            <div className="flex gap-3 justify-end">
              <button className="px-4 py-2 rounded-md border border-[var(--border)]" onClick={() => setAdjModalOpen(null)}>{t("cancel")}</button>
              <button className="btn-gold px-6 py-2 rounded-md" onClick={addAdjustment} data-testid="adj-save">{t("save")}</button>
            </div>
          </div>
        </div>
      )}
      <ConfirmDialogSlot />
    </div>
  );
}
